import { put, call, takeLatest } from 'redux-saga/effects';
import * as Api from './api';
import { SHOW_LOADER, HIDE_LOADER, SESSIONCONTEXT_DATA } from '@/common/actionConstants';
import { SET_VALIDATION_ERROR_MESSAGE } from '@/containers/SpockViewPage/actions/actionConstants';
import { ListingViewConstants } from '@/common/constants';
import { LOG_APPLICATON_ERROR } from '@/containers/WorkkardDetail/actions/actionConstants';
import { APP_ERROR_CODES, ErrorCode } from '@/containers/WorkkardDetail/constants';
import { logout } from '@/containers/WorkkardDetail/Saga/common';
import { hydrateUsers } from '@/common/sagas/userListSaga';
import { getOrgs } from '@/common/sagas/orgListSaga';
import { normalizeTabCountResponse, normalizeListingResponse } from '@/services/activityListing';
import {
  fetchActivityListingDataRequest,
  fetchActivityListingDataSuccess,
  fetchActivityListingDataFailure,
  updatePagingModel,
  resetActivityListingData,
  updateTabCount,
  activityListingBulkActionSuccess,
  getAcceptReassignDataSuccess,
  bulkValidationAcceptReassignActivitySuccess,
  bulkValidateActivityIdsChangeAcceptReassignRequestSuccess,
  activityListingBulkActionHandler,
  activityListingBulkActionConfirm,
  getAcceptReassignDataRequest,
  bulkValidationAcceptReassignActivityRequest,
  bulkAcceptReassignActivityRequest,
} from './slice';

export function* getActivityListingData(action) {
  try {
    yield put({ type: SHOW_LOADER });
    
    const { view, isScroll, ...apiPayload } = action.payload;
    const isListView = view === ListingViewConstants.ListView;

    if (!isScroll) {
      yield put(resetActivityListingData());
    }

    const response = yield call(Api.getActivityListingData, isListView, apiPayload.isSearch, apiPayload);

    if (response.status === 200) {
      const normalized = normalizeListingResponse(response, isScroll);
      
      yield put(updatePagingModel({
        Page: apiPayload.page,
        TotalCount: normalized.total,
        Rows: apiPayload.rows,
      }));

      yield put(fetchActivityListingDataSuccess({
        ...normalized,
        isScroll,
      }));

      const activityUserIds = (normalized.activitiesFullData || []).reduce((acc, item) => {
        if (item.activityAssigneeId) acc.push(item.activityAssigneeId);
        if (item.activityOwnerId) acc.push(item.activityOwnerId);
        return acc;
      }, []);
      if (activityUserIds.length > 0) {
        yield* hydrateUsers(activityUserIds);
      }

      const activityOrgIds = (normalized.activitiesFullData || []).reduce((acc, item) => {
        if (item.assigneeOrgId) acc.push(item.assigneeOrgId);
        if (item.ownerOrgId) acc.push(item.ownerOrgId);
        return acc;
      }, []);
      if (activityOrgIds.length > 0) {
        yield* getOrgs(activityOrgIds);
      }

      // ListActivities now returns open/close/unassigned counts on every
      // response (see normalizeListingResponse), so all three tabs refresh
      // from a single fetch instead of only the tab that was just opened.
      // watching_count isn't a backend concept - the "was this tab just
      // fetched" fallback below is the only source for it.
      const tabCountUpdate = {
        openCount: normalized.tabCounts.openCount,
        closeCount: normalized.tabCounts.closeCount,
        unassignedCount: normalized.tabCounts.unassignedCount,
      };
      if (apiPayload.status === 'watching') {
        tabCountUpdate.watchingCount = normalized.total || 0;
      }

      yield put(updateTabCount(tabCountUpdate));
    } else if (response.status === ErrorCode.RedirectToLogout) {
      yield* logout();
    } else if (APP_ERROR_CODES.indexOf(response.status) > -1) {
      yield put({
        type: LOG_APPLICATON_ERROR,
        payload: { status: response.status, isErrorScreen: true },
      });
      yield put(fetchActivityListingDataFailure({ status: response.status }));
    }
  } catch (error) {
    yield put(fetchActivityListingDataFailure({ error: error.message }));
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

export function* activityGridBulkActionHandler(action) {
  try {
    yield put({ type: SHOW_LOADER });
    let response = yield call(Api.activityGridBulkActionValidator, action.payload);
    if (response.status === 200) {
      response.type = action.payload.type;
      response.showConfirmationBox = true;
      yield put(activityListingBulkActionSuccess(response));
    } else if (response.status === ErrorCode.RedirectToLogout) {
      yield* logout();
    } else {
      yield put({
        type: LOG_APPLICATON_ERROR,
        payload: { status: response.status, isErrorScreen: false },
      });
    }
  } catch (error) {
    console.error('[BulkAction] Error:', error);
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

export function* activityGridBulkActionConfirmHandler(action) {
  try {
    yield put(activityListingBulkActionSuccess({ showConfirmationBox: false }));
    yield put({ type: SHOW_LOADER });
    let response = yield call(Api.activityGridBulkActionConfirm, action.payload.type, action.payload);
    if (response.status === 200) {
      window.location = response.url;
    } else if (response.status === ErrorCode.RedirectToLogout) {
      yield* logout();
    } else {
      yield put({
        type: LOG_APPLICATON_ERROR,
        payload: { status: response.status, isErrorScreen: false },
      });
    }
  } catch (error) {
    console.error('[BulkActionConfirm] Error:', error);
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

function* fetchAcceptReassignData(action) {
  try {
    yield put({ type: SHOW_LOADER });
    const response = yield call(Api.fetchAcceptReassignData, action.payload);
    if (response.status != 200) {
      const errorMessage = response.validationMessage || 'There is some problem with your request.';
      yield put({ type: SET_VALIDATION_ERROR_MESSAGE, payload: errorMessage });
    } else {
      yield put(getAcceptReassignDataSuccess(response.Data));
    }
  } catch (error) {
    yield put({
      type: SET_VALIDATION_ERROR_MESSAGE,
      payload: 'There is some problem with your request.',
    });
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

function* validateBulkAcceptReassignActivity(action) {
  try {
    yield put({ type: SHOW_LOADER });
    const response = yield call(Api.validateBulkAcceptReassignActivity, action.payload);
    if (response.status != 200) {
      const errorMessage = response.validationMessage || 'There is some problem with your request.';
      yield put({ type: SET_VALIDATION_ERROR_MESSAGE, payload: errorMessage });
    } else {
      yield put(bulkValidationAcceptReassignActivitySuccess(response.Data));
    }
  } catch (error) {
    yield put({
      type: SET_VALIDATION_ERROR_MESSAGE,
      payload: 'There is some problem with your request.',
    });
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

function* changeAcceptReassignActivity(action) {
  try {
    yield put({ type: SHOW_LOADER });
    const response = yield call(Api.changeAcceptReassignActivity, action.payload);
    if (response.status != 200) {
      const errorMessage = response.validationMessage || 'There is some problem with your request.';
      yield put({ type: SET_VALIDATION_ERROR_MESSAGE, payload: errorMessage });
    } else {
      yield put(bulkValidateActivityIdsChangeAcceptReassignRequestSuccess(response));
      window.location = response.url;
    }
  } catch (error) {
    yield put({
      type: SET_VALIDATION_ERROR_MESSAGE,
      payload: 'There is some problem with your request.',
    });
  } finally {
    yield put({ type: HIDE_LOADER });
  }
}

export default [
  takeLatest(fetchActivityListingDataRequest.type, getActivityListingData),
  takeLatest(activityListingBulkActionHandler.type, activityGridBulkActionHandler),
  takeLatest(activityListingBulkActionConfirm.type, activityGridBulkActionConfirmHandler),
  takeLatest(getAcceptReassignDataRequest.type, fetchAcceptReassignData),
  takeLatest(bulkValidationAcceptReassignActivityRequest.type, validateBulkAcceptReassignActivity),
  takeLatest(bulkAcceptReassignActivityRequest.type, changeAcceptReassignActivity),
];